<template>
    <div :class="cssClass">
        <nav class="side-rail">
            <ul>
                <li v-if="hasReadPermission" :class="{ active: dashboardNavActive }">
                    <router-link to="/"
                        ><NavigationButton icon="dashboard" v-tooltip.left-rail-tip="'Dashboard'"
                    /></router-link>
                </li>
                <li v-if="hasReadPermission" :class="{ active: customWorkNavActive }">
                    <router-link :to="{ name: 'my-custom-work' }"
                        ><NavigationButton icon="tune" v-tooltip.left-rail-tip="'My Custom Work'"
                    /></router-link>
                </li>
                <li :class="{ active: watchlistNavActive }">
                    <router-link to="/tracklist"
                        ><NavigationButton icon="bookmarks" v-tooltip.left-rail-tip="'TrackList'"
                    /></router-link>
                </li>
            </ul>
            <ul class="side-rail-lower-icons">
                <li :class="{ admin: true, active: toolsNavActive }" v-if="showToolsLink">
                    <router-link to="/manage/tools">
                        <NavigationButton icon="construction" v-tooltip.left-rail-tip="'Tools'" />
                    </router-link>
                </li>
                <li
                    :class="{ admin: true, active: researchNavActive }"
                    v-if="showManageLinksInLeftRail && showResearchLink"
                >
                    <router-link :to="{ name: 'manage-research-page' }">
                        <NavigationButton icon="science" v-tooltip.left-rail-tip="'Research'" />
                    </router-link>
                </li>
                <li
                    :class="{ admin: true, active: manageNavActive }"
                    v-if="showManageLinksInLeftRail && showManageLink"
                >
                    <router-link to="/manage">
                        <NavigationButton icon="playlist_add_check" v-tooltip.left-rail-tip="'Manage'" />
                    </router-link>
                </li>
                <li
                    v-if="showManageLinksInLeftRail && showAdminAccountManagementLinks"
                    :class="{ admin: true, active: clientsNavActive }"
                >
                    <router-link to="/admin/clients">
                        <NavigationButton icon="group" v-tooltip.left-rail-tip="'Clients'" />
                    </router-link>
                </li>
                <li id="user-avatar">
                    <TriggerWithMenu id="account-menu">
                        <template #default>
                            <UserAvatar />
                        </template>
                        <template #menu>
                            <ul>
                                <li v-if="showManageLink">
                                    <router-link to="/manage">Manage</router-link>
                                </li>
                                <li v-if="showAdminAccountManagementLinks">
                                    <router-link to="/admin/reports">Admin: Reports</router-link>
                                </li>
                                <li v-if="showLogins">
                                    <router-link to="/admin/logins">Admin: Logins</router-link>
                                </li>
                                <li v-if="showAdminAccountManagementLinks">
                                    <router-link to="/admin/clients">Admin: Clients</router-link>
                                </li>
                                <li v-if="showResearchLink" class="account-menu-divider">
                                    <router-link :to="{ name: 'manage-research-page' }"> Admin: Research </router-link>
                                </li>
                                <li v-if="showAiLink" class="account-menu-divider">
                                    <router-link :to="{ name: 'manage-ai-home' }"> Admin: AI </router-link>
                                </li>
                                <li v-if="showUserManagement" class="account-menu-divider">
                                    <router-link to="/organization">User Accounts</router-link>
                                </li>
                                <li class="account-menu-divider">
                                    <router-link to="/account#Profile">Profile</router-link>
                                </li>
                                <li>
                                    <router-link to="/account#Password">Password</router-link>
                                </li>
                                <li class="account-menu-divider">
                                    <a href="#" @click.prevent="logout">Logout</a>
                                </li>
                            </ul>
                        </template>
                    </TriggerWithMenu>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script>
import AuthenticationService from '@/services/authenticationService'
import {
    hasAdminRole,
    hasAiAdminRole,
    hasAnyEditRole,
    hasDataEntryRole,
    hasInHouseDataEntryRole,
    hasInternalReadRole,
    hasInterviewersRole,
    hasManageOrganizationRole,
    hasMatchingRole,
    hasReadRole,
    hasReadPermission,
    hasResearchRole,
    hasSalesAdminRole,
    jwt,
} from '@/utils/jwt.js'

import NavigationButton from '@/components/shared/navigation-button.vue'
import TriggerWithMenu from '@/components/shared/trigger-with-menu.vue'
import UserAvatar from '@/components/shared/user-avatar.vue'

export default {
    components: {
        NavigationButton,
        TriggerWithMenu,
        UserAvatar,
    },
    computed: {
        hasReadPermission() {
            return hasReadRole()
        },
        showLogins() {
            return hasAdminRole() || hasInternalReadRole()
        },
        showUserManagement() {
            return hasManageOrganizationRole()
        },
        cssClass() {
            return { 'left-rail': true }
        },
        customWorkNavActive() {
            return this.$route.name === 'my-custom-work'
        },
        dashboardNavActive() {
            return this.$route.path === '/'
        },
        watchlistNavActive() {
            return this.$route.path === '/tracklist'
        },
        manageNavActive() {
            return this.$route.path.startsWith('/manage') && !this.toolsNavActive && !this.researchNavActive
        },
        toolsNavActive() {
            return this.$route.path.startsWith('/manage/tools/')
        },
        researchNavActive() {
            return this.$route.path === '/manage/research'
        },
        clientsNavActive() {
            return this.$route.path === '/admin/clients'
        },
        showAdminAccountManagementLinks() {
            return hasSalesAdminRole()
        },
        showAiLink() {
            return hasAiAdminRole() || hasResearchRole() || hasSalesAdminRole()
        },
        showManageLinksInLeftRail() {
            return hasDataEntryRole() || hasInHouseDataEntryRole() || hasMatchingRole()
        },
        showManageLink() {
            return hasAnyEditRole()
        },
        showResearchLink() {
            return hasAdminRole() || hasResearchRole()
        },
        showToolsLink() {
            return hasAdminRole() || hasInterviewersRole() || hasResearchRole() || hasSalesAdminRole()
        },
    },
    data() {
        return {
            showingAccountMenu: false,
        }
    },
    methods: {
        toggleAccountMenu() {
            this.showingAccountMenu = !this.showingAccountMenu
        },
        hideAccountMenu() {
            this.showingAccountMenu = false
        },
        async logout() {
            const { ok } = await AuthenticationService.logout()

            if (ok) {
                this.$router.push('/login')
            }
        },
    },
}
</script>
<style>
.left-rail {
    height: calc(100vh - var(--top-rail-height));
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;

    .side-rail {
        li.active .navigation-button {
            background-color: var(--color-active);
            color: var(--color-light-text);
        }
    }
    li.admin.active .navigation-button {
        background-color: transparent;
        border: 1px solid var(--color-active);
        color: var(--color-active);
    }
}

#user-avatar {
    display: relative;
}

#account-menu .trigger-with-menu-menu {
    background-color: white;
    bottom: -4px;
    color: var(--color-hint-text);
    font-family: Lato;
    font-size: var(--font-size-normal-text);
    left: 28px;
    letter-spacing: 0.24px;
    line-height: 22.5px;
    padding: 16px;
    right: initial;
    text-align: left;
    top: initial;
    white-space: nowrap;
    width: 304px;

    li {
        margin: 0;
        padding: 8px 0;

        a {
            display: block;
        }

        + li.account-menu-divider {
            border-top: 1px solid var(--color-alpha-1);
        }
    }
}
</style>
